// Tenant-Admin/Subscription/PaymentCheckoutModal.jsx
import React, { useState, useEffect } from 'react';
import { X, RefreshCw, AlertCircle, Lock } from 'lucide-react';
import api from '../../api';

// ✅ Load Razorpay script dynamically - EXPORTED
export const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// ✅ Open Razorpay checkout - EXPORTED
export const openRazorpayCheckout = (options, plan, action) => {
  const razorpay = new window.Razorpay(options);
  razorpay.open();
  return razorpay;
};

const PaymentCheckoutModal = ({ isOpen, onClose, plan, action, tenantId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderData, setOrderData] = useState(null);

  useEffect(() => {
    if (isOpen && plan) {
      initializePayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, plan]);

  const initializePayment = async () => {
    try {
      setLoading(true);
      setError(null);

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setError('Could not connect to the payment network. Please refresh and try again.');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      const response = await api.post('/api/tenant-admin/subscription/payment/create-order', {
        planId: plan.planId,
        action: action || 'new',
        tenantId: tenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const currentOrder = response.data.data;
        setOrderData(currentOrder);
        openRazorpayCheckoutWithData(currentOrder, plan, action);
      } else {
        setError(response.data.message || 'We could not generate your checkout token.');
      }
    } catch (err) {
      console.error('❌ Payment initialization error:', err);
      setError(err.response?.data?.message || 'Something went wrong while setting up checkout.');
    } finally {
      setLoading(false);
    }
  };

  const openRazorpayCheckoutWithData = (orderData, plan, action) => {
    const companyName = process.env.COMPANY_NAME || 'Truyon';
    
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: Math.round(orderData.amount * 100),
      currency: orderData.currency || 'INR',
      name: companyName,
      description: `${plan?.name || 'Plan'} - ${action || 'Subscription update'}`,
      order_id: orderData.orderId,
      // Removed prefill data completely to let Razorpay handle customer autofill preference
      theme: {
        color: '#4f46e5',
      },
      modal: {
        ondismiss: function() {
          setLoading(false);
        }
      },
      handler: function(response) {
        verifyPayment(response, orderData);
      }
    };

    const razorpay = new window.Razorpay(options);
    razorpay.open();
  };

  const verifyPayment = async (razorpayResponse, orderData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.post('/api/tenant-admin/subscription/payment/verify', {
        razorpayOrderId: razorpayResponse.razorpay_order_id,
        razorpayPaymentId: razorpayResponse.razorpay_payment_id,
        razorpaySignature: razorpayResponse.razorpay_signature,
        paymentId: orderData.paymentId,
        tenantId: tenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (onSuccess) {
          await onSuccess(response.data.data);
        }
        setTimeout(() => {
          onClose();
        }, 1200);
      } else {
        setError(response.data.message || 'Your payment verification failed.');
      }
    } catch (err) {
      console.error('❌ Payment verification error:', err);
      setError(err.response?.data?.message || 'Could not verify the transaction status.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const priceBreakdown = orderData?.priceBreakdown || {};
  const isDiscounted = priceBreakdown.isSpecialPrice || false;
  const companyName = process.env.REACT_APP_COMPANY_NAME || 'Truyon';

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-sm w-full max-h-[90vh] overflow-y-auto shadow-2xl flex flex-col border border-slate-100">
        
        {/* Header Block */}
        <div className="sticky top-0 bg-white border-b border-slate-100 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Secure Checkout</h2>
            <p className="text-xs text-slate-400 mt-0.5 truncate max-w-[240px]">
              {plan?.name || 'Plan'} Tier • {action || 'Update'}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition"
            disabled={loading}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-5 space-y-4">
          
          {/* Active Processing Loader */}
          {loading && (
            <div className="text-center py-6 flex flex-col items-center justify-center">
              <RefreshCw className="w-7 h-7 text-indigo-600 animate-spin mb-3" />
              <h3 className="text-xs font-bold text-slate-800">Setting up secure gateway...</h3>
              <p className="text-[11px] text-slate-400 mt-0.5">Please leave this payment view open.</p>
            </div>
          )}

          {/* Error Banner */}
          {error && !loading && (
            <div className="p-4 bg-rose-50 border border-rose-100 rounded-xl space-y-2">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <span className="text-xs font-bold text-rose-900 block">Transaction Error</span>
                  <p className="text-xs text-rose-700 leading-relaxed">{error}</p>
                </div>
              </div>
              <button
                onClick={initializePayment}
                className="w-full py-1.5 text-center bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-lg transition"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Interactive Invoice Breakdown */}
          {orderData && !loading && !error && (
            <div className="space-y-4 animate-fadeIn">
              
              {/* Reference Tier Tag */}
              <div className="bg-slate-50 rounded-xl p-3 border border-slate-200/60 flex items-center justify-between text-xs">
                <div>
                  <span className="text-[10px] text-slate-400 font-medium block uppercase tracking-wider">Tier Name</span>
                  <span className="font-bold text-slate-800">{plan?.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-[10px] text-slate-400 font-medium block uppercase tracking-wider">Process Type</span>
                  <span className="font-bold text-indigo-600 capitalize">{action || 'New'}</span>
                </div>
              </div>

              {/* Price Details Block */}
              <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
                <div className="px-3.5 py-2 bg-slate-50 border-b border-slate-200">
                  <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">Pricing Summary</span>
                </div>
                
                <div className="p-3.5 space-y-2.5 text-xs font-medium">
                  {isDiscounted && (
                    <div className="flex justify-between text-slate-400">
                      <span>Standard Value</span>
                      <span className="line-through">₹{priceBreakdown.originalAmount?.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-slate-600">
                    <span>Base Account Rate</span>
                    <span>₹{priceBreakdown.subtotal?.toFixed(2)}</span>
                  </div>
                  
                  {isDiscounted && (
                    <div className="flex justify-between text-emerald-600">
                      <span>Promo Discount</span>
                      <span>-₹{priceBreakdown.discountAmount?.toFixed(2)} ({priceBreakdown.discountPercentage?.toFixed(0)}%)</span>
                    </div>
                  )}
                  
                  {priceBreakdown.taxAmount > 0 && (
                    <div className="flex justify-between text-slate-500">
                      <span>{priceBreakdown.taxName || 'GST'} ({priceBreakdown.taxPercentage || 0}%)</span>
                      <span>₹{priceBreakdown.taxAmount?.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {priceBreakdown.isProrated && (
                    <div className="flex justify-between text-amber-700 bg-amber-50/50 p-1 rounded">
                      <span>Prorated Adjustments</span>
                      <span>₹{priceBreakdown.proratedDetails?.proratedAmount?.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between text-sm font-black pt-2.5 border-t border-slate-200 text-slate-900">
                    <span>Final Total Due</span>
                    <span className="text-indigo-600">₹{priceBreakdown.total?.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Security Shield Indicator */}
              <div className="space-y-3 pt-1">
                <div className="flex items-center gap-1.5 text-[10px] text-slate-400 font-medium justify-center bg-slate-50 py-1.5 rounded-lg border border-slate-100">
                  <Lock className="w-3 h-3 text-slate-400" />
                  <span>Payments encrypted and protected by Razorpay</span>
                </div>
                
                <p className="text-center text-[10px] text-slate-300 font-medium tracking-wide">
                  Account Infrastructure powered by {companyName}
                </p>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentCheckoutModal;